import { ReactNode, RefObject, useEffect, useId, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import styled from 'styled-components';

export default function OrderAddressText({ address, individual = false, children, overflowTarget, focusable = true }: {
  address: string; individual?: boolean; children?: ReactNode; overflowTarget?: RefObject<HTMLElement | null>; focusable?: boolean;
}) {
  const anchor = useRef<HTMLSpanElement>(null);
  const tooltipRef = useRef<HTMLDivElement>(null);
  const id = useId();
  const [position, setPosition] = useState<{ left: number; top?: number; bottom?: number } | null>(null);
  
  // Refs to manage hover/focus state and timers
  const hoverRef = useRef({ anchor: false, tooltip: false });
  const timerRef = useRef<NodeJS.Timeout | null>(null);
  const selectionDragRef = useRef(false);
  
  const hasTooltipSelection = () => {
    const selection = window.getSelection();
    if (!selection || selection.toString().length === 0) return false;
    const anchorNode = selection.anchorNode;
    const focusNode = selection.focusNode;
    return tooltipRef.current && (tooltipRef.current.contains(anchorNode) || tooltipRef.current.contains(focusNode));
  };
  
  useEffect(() => {
    const target = overflowTarget?.current || anchor.current;
    if (target && target.scrollWidth <= target.clientWidth + 1) setPosition(null);
  }, [address]);

  const show = () => {
    const target = overflowTarget?.current || anchor.current;
    if (!address || !anchor.current || !target || target.scrollWidth <= target.clientWidth + 1) {
      setPosition(null);
      return;
    }
    const rect = anchor.current.getBoundingClientRect();
    const width = Math.min(parseFloat(getComputedStyle(document.documentElement).fontSize || '16') * 32, window.innerWidth - 32);
    setPosition({ 
      left: Math.max(16, Math.min(rect.left, window.innerWidth - width - 16)),
      ...(rect.bottom > window.innerHeight - 120 ? { bottom: window.innerHeight - rect.top + 8 } : { top: rect.bottom + 8 }) 
    });
    hoverRef.current.tooltip = false; selectionDragRef.current=false;
  };

  const cancelClose = () => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  };

  const scheduleClose = () => {
    if (timerRef.current) return;
    timerRef.current = setTimeout(() => {
      timerRef.current = null;
      // Check conditions before closing
      const isHoveringAnchor = hoverRef.current.anchor;
      const isHoveringTooltip = hoverRef.current.tooltip;
      const isActiveElement = !!anchor.current?.contains(document.activeElement);
      const isDragging = selectionDragRef.current;
      
      
      if (isHoveringAnchor || isHoveringTooltip || isActiveElement || isDragging || hasTooltipSelection()) {
        return;
      }
      setPosition(null);
    }, 220);
  };

  const handleMouseEnterAnchor = () => {
    hoverRef.current.anchor = true;
    cancelClose();
    show();
  };

  const handleMouseLeaveAnchor = (e: React.MouseEvent) => {
    if (e.relatedTarget instanceof Node && anchor.current?.contains(e.relatedTarget)) return;
    hoverRef.current.anchor = false;
    if (!hoverRef.current.tooltip) scheduleClose();
  };

  const handleMouseEnterTooltip = () => {
    hoverRef.current.tooltip = true;
    cancelClose();
  };

  const handleMouseLeaveTooltip = (e: React.MouseEvent) => {
    if (e.relatedTarget instanceof Node && tooltipRef.current?.contains(e.relatedTarget)) return;
    hoverRef.current.tooltip = false;
    if (!hoverRef.current.anchor) scheduleClose();
  };

  const handleMouseDownTooltip = () => {
    selectionDragRef.current = true;
    cancelClose();
  };

  const handleMouseUpTooltip = () => {
    selectionDragRef.current = false;
    // Check if there's selected text inside tooltip
    if (hasTooltipSelection()) {
      return;
    }
    scheduleClose();
  };

  const dismiss = () => {
    cancelClose();
    hoverRef.current.anchor = false;
    hoverRef.current.tooltip = false;
    selectionDragRef.current = false;
    setPosition(null);
  };

  const handleDocumentMouseDown = (e: MouseEvent) => {
    if (!anchor.current || !tooltipRef.current) return;
    const target = e.target as Node;
    if (!anchor.current.contains(target) && !tooltipRef.current.contains(target)) {
      dismiss();
    }
  };

  const handleKeyDown = (e: KeyboardEvent) => {
    if (e.key === 'Escape') dismiss();
  };

  useEffect(() => {
    if (!position) return;
    
    // Add event listeners
    const hide = dismiss;
    const handleScroll = (e: Event) => {
      if (e.target instanceof Node && tooltipRef.current?.contains(e.target)) return;
      if (selectionDragRef.current) return;
      hide();
    };
    
    window.addEventListener('scroll', handleScroll, true);
    window.addEventListener('resize', hide);
    document.addEventListener('mousedown', handleDocumentMouseDown);
    document.addEventListener('mouseup', handleMouseUpTooltip);
    document.addEventListener('keydown', handleKeyDown);
    
    return () => {
      // Cleanup
      cancelClose();
      window.removeEventListener('scroll', handleScroll, true);
      window.removeEventListener('resize', hide);
      document.removeEventListener('mousedown', handleDocumentMouseDown);
      document.removeEventListener('mouseup', handleMouseUpTooltip);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [position]);

  return (
    <>
      <Text 
        ref={anchor} 
        tabIndex={focusable && address ? 0 : undefined} 
        aria-describedby={position ? id : undefined}
        onMouseEnter={handleMouseEnterAnchor}
        onMouseLeave={handleMouseLeaveAnchor}
        onFocus={()=>{cancelClose();show();}}
        onBlur={() => {
          // Only schedule close if not dragging
          if (!selectionDragRef.current) scheduleClose();
        }}
        onKeyDown={(event) => { 
          if (event.key === 'Escape') dismiss(); 
        }}
      >
        {individual && <small>개별배송 · </small>}{children ?? (address || '—')}
      </Text>
      
      {position && createPortal(
        <TooltipPosition 
          style={position}
          ref={tooltipRef}
          onMouseEnter={handleMouseEnterTooltip}
          onMouseLeave={handleMouseLeaveTooltip}
          onMouseDown={handleMouseDownTooltip}
        >
          <Tooltip 
            role="tooltip" 
            id={id} 
          >
            {address}
          </Tooltip>
        </TooltipPosition>, 
        document.body
      )}
    </>
  );
}

const Text = styled.span`
  display: block; width: 100%; min-width: 0; overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
  small { font-size: var(--text-caption); color: var(--color-text-secondary); }
  &:focus-visible { outline: 2px solid var(--color-focus-ring); outline-offset: -2px; }
`;

const TooltipPosition = styled.div`
  position: fixed;
  z-index: 10000;
  width: max-content;
  max-width: min(32rem, calc(100vw - var(--space-8)));
  overflow: visible;
  &::before {
    content: '';
    position: absolute;
    top: calc(-1 * var(--space-2));
    bottom: calc(-1 * var(--space-2));
    left: 0;
    right: 0;
    z-index: -1;
  }
`;

const Tooltip = styled.div`
  position: relative;
  width: max-content; 
  max-width: min(32rem, calc(100vw - var(--space-8)));
  box-sizing: border-box; 
  padding: var(--space-3) var(--space-4); 
  pointer-events: auto;
  background: var(--color-bg-elevated); 
  color: var(--color-text-primary); 
  border: 1px solid var(--color-border-strong);
  border-radius: var(--radius-sm); 
  font-family: var(--font-family-base); 
  font-size: var(--text-body); 
  line-height: 1.5;
  white-space: normal; 
  overflow-wrap: anywhere; 
  user-select: text; 
  cursor: text;
  max-height: calc(100vh - var(--space-8)); 
  overflow-y: auto;
`;
